import { FLOW_CONTROL_SOCKET_TYPE } from "../types";

/**
 * Check if two socket types are compatible for connection.
 *
 * Used by both the workflow editor (for drag-connect) and the Nami
 * scratchpad (for agent-proposed edges). Both call sites need identical
 * semantics, so the logic lives here rather than duplicated per-side.
 */
export function areSocketTypesCompatible(
  sourceType: string,
  targetType: string,
): boolean {
  // Flow control INPUT sockets accept ANY type (acts as a "wait for completion" signal)
  if (targetType === FLOW_CONTROL_SOCKET_TYPE) {
    return true;
  }

  // Flow control OUTPUT sockets can ONLY connect to flow control inputs
  if (sourceType === FLOW_CONTROL_SOCKET_TYPE) {
    return false;
  }

  // 'any' is compatible with everything
  if (sourceType === "any" || targetType === "any") {
    return true;
  }

  // Exact match
  if (sourceType === targetType) {
    return true;
  }

  // Arrays - check element types (must come before json/string checks)
  if (sourceType.startsWith("array<") && targetType.startsWith("array<")) {
    const sourceElementType = sourceType.slice(6, -1);
    const targetElementType = targetType.slice(6, -1);
    return areSocketTypesCompatible(sourceElementType, targetElementType);
  }

  // Array output to single input (fanout)
  if (sourceType.startsWith("array<")) {
    const sourceElementType = sourceType.slice(6, -1);
    // Every column of an ingest foreign table is TEXT, so a CDC observe step
    // types its column outputs `array<string>` even for a numeric field, and the
    // socket wired to one receives `"3"`. `INTEGER_INPUT` / `QUANTITY_INPUT`
    // exist for exactly that reason and coerce at the boundary, rejecting real
    // junk. Without this the most common ingest wiring there is — a source
    // column into a numeric socket — is unconnectable in the editor and
    // unsavable as an active workflow.
    //
    // Deliberately scoped to unwrapping a batch column. A plain scalar
    // `string` is NOT accepted by a numeric socket: that shape carries no
    // TEXT-source excuse, and treating it as numeric would wave through a real
    // mismatch (see the canonical-socket regression in
    // `canonicalizeStepSockets.test.ts`).
    if (sourceElementType === "string" && targetType === "number") return true;
    return areSocketTypesCompatible(sourceElementType, targetType);
  }

  // Single input to array output (fan-in)
  if (targetType.startsWith("array<")) {
    const targetElementType = targetType.slice(6, -1);
    return areSocketTypesCompatible(sourceType, targetElementType);
  }

  // JSON accepts primitives and entity references
  if (targetType === "json") {
    if (sourceType.startsWith("entity_reference")) {
      return true;
    }
    return ["string", "number", "boolean", "json", "uuid", "date"].includes(
      sourceType,
    );
  }

  // String accepts implicit-conversion types
  if (targetType === "string") {
    return ["string", "number", "boolean", "uuid", "date"].includes(sourceType);
  }

  // Entity reference compatibility
  if (
    sourceType.startsWith("entity_reference") &&
    targetType.startsWith("entity_reference")
  ) {
    if (
      sourceType === "entity_reference" ||
      targetType === "entity_reference"
    ) {
      return true;
    }
    return sourceType === targetType;
  }

  return false;
}
